import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Divider,
  IconButton,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  InputAdornment,
  Fade,
  Zoom,
  TablePagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Computer as ComputerIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  Warning as WarningIcon,
  Security as SecurityIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  Shield as ShieldIcon,
  BugReport as BugReportIcon,
  Inventory as PackageIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import vulnerabilityApiService from '../../services/vulnerabilities/VulnerabilityApiService';
import dashboardApiService from '../../services/dashboard/DashboardApiService';

const AgentVulnerabilities = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [agent, setAgent] = useState(null);
  const [vulnerabilityData, setVulnerabilityData] = useState(null);
  const [rawVulnerabilities, setRawVulnerabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingVulns, setLoadingVulns] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState(searchParams.get('severity') || 'all');
  const [sortBy, setSortBy] = useState('cvss');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedVuln, setExpandedVuln] = useState(null);

  const fetchAgentDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const agentData = await dashboardApiService.getAgentById(id);
      setAgent(agentData);
    } catch (err) {
      setError(`Failed to fetch agent details: ${err.message}`);
      console.error('Agent details fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchVulnerabilityData = useCallback(async () => {
    try {
      setLoadingVulns(true);
      const vulnResponse = await vulnerabilityApiService.getVulnerabilitiesByAgent(id);
      const vulnerabilities = Array.isArray(vulnResponse) ? vulnResponse : [];
      
      // Debug: Log first vulnerability to see data structure
      if (vulnerabilities.length > 0) {
        console.log('Sample vulnerability data:', JSON.stringify(vulnerabilities[0], null, 2));
      }
      
      const processedData = vulnerabilityApiService.processVulnerabilityData(vulnerabilities);
      
      setRawVulnerabilities(vulnerabilities);
      setVulnerabilityData(processedData);
    } catch (err) {
      console.error('Vulnerability data fetch error:', err);
    } finally {
      setLoadingVulns(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchAgentDetails();
      fetchVulnerabilityData();
    }
  }, [id, fetchAgentDetails, fetchVulnerabilityData]);

  useEffect(() => {
    // Update severity filter when URL parameter changes
    const urlSeverity = searchParams.get('severity');
    if (urlSeverity && urlSeverity !== severityFilter) {
      setSeverityFilter(urlSeverity);
    }
  }, [searchParams, severityFilter]);

  // Generate fix instructions based on package and OS
  const getFixInstructions = (vuln) => {
    if (!vuln) {
      console.warn('getFixInstructions called with null/undefined vuln');
      return { commands: ['# No vulnerability data available'], docs: [] };
    }
    
    const packageName = vuln?.package?.name || 'package';
    const packageVersion = vuln?.package?.version || '';
    const cveId = vuln?.vulnerability?.cve || vuln?.vulnerability?.id || 'CVE-XXXX';
    
    // Detect OS type from agent info or package manager
    const osName = agent?.os?.name?.toLowerCase() || '';
    const osPlatform = agent?.os?.platform?.toLowerCase() || '';
    const isDebian = osName.includes('ubuntu') || osName.includes('debian') || osPlatform.includes('ubuntu') || osPlatform.includes('debian');
    const isRHEL = osName.includes('centos') || osName.includes('rhel') || osName.includes('fedora') || osPlatform.includes('centos') || osPlatform.includes('rhel');
    
    console.log('Fix instructions for:', { packageName, cveId, osName, osPlatform, isDebian, isRHEL });
    
    let commands = [];
    let docs = [];
    
    if (isDebian) {
      commands = [
        `# Update package list`,
        `sudo apt update`,
        ``,
        `# Upgrade specific package`,
        `sudo apt install --only-upgrade ${packageName}`,
        ``,
        `# Or upgrade all packages`,
        `sudo apt upgrade -y`
      ];
    } else if (isRHEL) {
      commands = [
        `# Update specific package`,
        `sudo yum update ${packageName}`,
        ``,
        `# Or use dnf (newer systems)`,
        `sudo dnf upgrade ${packageName}`,
        ``,
        `# Update all packages`,
        `sudo yum update -y`
      ];
    } else {
      commands = [
        `# Generic update commands:`,
        ``,
        `# For Debian/Ubuntu:`,
        `sudo apt update && sudo apt upgrade ${packageName}`,
        ``,
        `# For RHEL/CentOS:`,
        `sudo yum update ${packageName}`,
        ``,
        `# For Alpine:`,
        `sudo apk upgrade ${packageName}`
      ];
    }
    
    // Extract documentation links from references field
    const references = vuln?.vulnerability?.reference || vuln?.vulnerability?.references || '';
    
    if (references && typeof references === 'string') {
      // Split by comma and extract URLs
      const refLinks = references.split(',').map(ref => ref.trim()).filter(ref => ref.startsWith('http'));
      docs = refLinks.map(link => `📚 ${link}`);
    }
    
    // If no references found, add default documentation links based on OS
    if (docs.length === 0 && cveId !== 'CVE-XXXX') {
      if (isDebian) {
        docs = [
          `📚 Ubuntu Security: https://ubuntu.com/security/${cveId}`,
          `📚 Debian Security: https://security-tracker.debian.org/tracker/${cveId}`
        ];
      } else if (isRHEL) {
        docs = [
          `📚 Red Hat CVE Database: https://access.redhat.com/security/cve/${cveId}`,
          `📚 CentOS Security: https://wiki.centos.org/Security`
        ];
      } else {
        docs = [
          `📚 NVD Database: https://nvd.nist.gov/vuln/detail/${cveId}`,
          `📚 CVE Details: https://www.cvedetails.com/cve/${cveId}`
        ];
      }
    }
    
    return { commands, docs };
  };

  const getFilteredAndSortedVulnerabilities = () => {
    if (!rawVulnerabilities || rawVulnerabilities.length === 0) return [];
    
    let filtered = rawVulnerabilities.filter(vuln => {
      const matchesSearch = vuln.vulnerability?.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           vuln.vulnerability?.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           vuln.package?.name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (severityFilter === 'all') return matchesSearch;
      
      const vulnSeverity = vuln.vulnerability?.severity?.toLowerCase() || 'info';
      return matchesSearch && vulnSeverity === severityFilter.toLowerCase();
    });
    
    // Sort vulnerabilities
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'cvss':
          return (b.vulnerability?.score?.base || 0) - (a.vulnerability?.score?.base || 0);
        case 'severity':
          const severityOrder = { critical: 4, high: 3, medium: 2, low: 1, info: 0 };
          const aSev = severityOrder[a.vulnerability?.severity?.toLowerCase()] || 0;
          const bSev = severityOrder[b.vulnerability?.severity?.toLowerCase()] || 0;
          return bSev - aSev;
        case 'detected':
          return new Date(b.vulnerability?.detected_at || 0) - new Date(a.vulnerability?.detected_at || 0);
        case 'package':
          return (a.package?.name || '').localeCompare(b.package?.name || '');
        default:
          return 0;
      }
    });
    
    return filtered;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const getSeverityColor = (severity) => {
    return vulnerabilityApiService.getSeverityBadgeColor(severity);
  };

  const getSeverityStats = () => {
    if (!vulnerabilityData) return { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    return vulnerabilityData.severityStats;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSeverityCardClick = (severity) => {
    setSeverityFilter(severity);
    setPage(0);
    // Update URL parameter
    const newSearchParams = new URLSearchParams(searchParams);
    if (severity === 'all') {
      newSearchParams.delete('severity');
    } else {
      newSearchParams.set('severity', severity);
    }
    navigate(`/agents/${id}/vulnerabilities?${newSearchParams.toString()}`, { replace: true });
  };

  const refreshData = () => {
    fetchAgentDetails();
    fetchVulnerabilityData();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading agent details...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={refreshData}
          startIcon={<RefreshIcon />}
          sx={{ mr: 2 }}
        >
          Retry
        </Button>
        <Button 
          variant="outlined" 
          onClick={() => navigate(`/agents/${id}`)}
          startIcon={<ArrowBackIcon />}
        >
          Back to Agent Details
        </Button>
      </Box>
    );
  }

  if (!agent) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Agent not found</Typography>
        <Typography variant="body2">The requested agent could not be found.</Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate('/agents')}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Agents
        </Button>
      </Box>
    );
  }

  const filteredVulnerabilities = getFilteredAndSortedVulnerabilities();
  const paginatedVulnerabilities = filteredVulnerabilities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const stats = getSeverityStats();

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Breadcrumbs */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Breadcrumbs>
          <Link 
            color="inherit" 
            onClick={() => navigate('/agents')}
            sx={{ cursor: 'pointer' }}
          >
            Agents
          </Link>
          <Link 
            color="inherit" 
            onClick={() => navigate(`/agents/${id}`)}
            sx={{ cursor: 'pointer' }}
          >
            {agent.name}
          </Link>
          <Typography color="text.primary">Vulnerabilities</Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton 
            onClick={() => navigate(`/agents/${id}`)}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          <IconButton 
            onClick={refreshData}
            size="small"
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Agent Info Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: '#f57c00', width: 48, height: 48 }}>
              <BugReportIcon />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Vulnerabilities - {agent.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Agent ID: {agent.id} • {agent.ip} • {agent.os.name} {agent.os.version}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip label={`${vulnerabilityData?.totalVulnerabilities || 0} Total Vulnerabilities`} color="warning" />
              <Chip 
                label={`${stats.critical + stats.high} Critical & High`} 
                sx={{ bgcolor: '#ffebee', color: '#d32f2f' }} 
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Severity Overview Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              border: severityFilter === 'all' ? '2px solid #1976d2' : '1px solid #e0e0e0',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
              transition: 'all 0.2s ease'
            }}
            onClick={() => handleSeverityCardClick('all')}
          >
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                {vulnerabilityData?.totalVulnerabilities || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All Vulnerabilities
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              border: severityFilter === 'critical' ? '2px solid #d32f2f' : '1px solid #e0e0e0',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
              transition: 'all 0.2s ease'
            }}
            onClick={() => handleSeverityCardClick('critical')}
          >
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
                {stats.critical}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Critical
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              border: severityFilter === 'high' ? '2px solid #f57c00' : '1px solid #e0e0e0',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
              transition: 'all 0.2s ease'
            }}
            onClick={() => handleSeverityCardClick('high')}
          >
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ color: '#f57c00', fontWeight: 'bold' }}>
                {stats.high}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                High
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              border: severityFilter === 'medium' ? '2px solid #fbc02d' : '1px solid #e0e0e0',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
              transition: 'all 0.2s ease'
            }}
            onClick={() => handleSeverityCardClick('medium')}
          >
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ color: '#fbc02d', fontWeight: 'bold' }}>
                {stats.medium}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Medium
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              border: severityFilter === 'low' ? '2px solid #388e3c' : '1px solid #e0e0e0',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
              transition: 'all 0.2s ease'
            }}
            onClick={() => handleSeverityCardClick('low')}
          >
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" sx={{ color: '#388e3c', fontWeight: 'bold' }}>
                {stats.low}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Low
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search vulnerabilities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Severity Filter</InputLabel>
                <Select
                  value={severityFilter}
                  onChange={(e) => handleSeverityCardClick(e.target.value)}
                  label="Severity Filter"
                  startAdornment={<FilterListIcon sx={{ mr: 1, color: 'action.active' }} />}
                >
                  <MenuItem value="all">All Severities</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Sort By"
                  startAdornment={<SortIcon sx={{ mr: 1, color: 'action.active' }} />}
                >
                  <MenuItem value="cvss">CVSS Score</MenuItem>
                  <MenuItem value="severity">Severity</MenuItem>
                  <MenuItem value="detected">Detection Date</MenuItem>
                  <MenuItem value="package">Package Name</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                {filteredVulnerabilities.length} vulnerabilities found
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Vulnerabilities Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loadingVulns ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress size={32} />
              <Typography variant="body2" sx={{ ml: 2 }}>Loading vulnerabilities...</Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>CVE ID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Severity</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>CVSS Score</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Package</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Fix Instructions</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Detected</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedVulnerabilities.map((vuln, index) => {
                      const severity = vuln.vulnerability?.severity || 'Info';
                      const isExpanded = expandedVuln === index;
                      
                      return (
                        <React.Fragment key={index}>
                          <TableRow hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <BugReportIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                  {vuln.vulnerability?.cve || vuln.vulnerability?.id || 'N/A'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={severity}
                                size="small"
                                sx={{
                                  bgcolor: getSeverityColor(severity) + '20',
                                  color: getSeverityColor(severity),
                                  fontWeight: 600,
                                  border: `1px solid ${getSeverityColor(severity)}40`
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {vuln.vulnerability?.score?.base?.toFixed(1) || 'N/A'}
                                </Typography>
                                {vuln.vulnerability?.score?.base && (
                                  <LinearProgress
                                    variant="determinate"
                                    value={(vuln.vulnerability.score.base / 10) * 100}
                                    sx={{
                                      width: 60,
                                      height: 6,
                                      borderRadius: 3,
                                      bgcolor: '#e0e0e0',
                                      '& .MuiLinearProgress-bar': {
                                        bgcolor: getSeverityColor(severity)
                                      }
                                    }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PackageIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2">
                                  {vuln.package?.name || 'Unknown'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Tooltip 
                                title={
                                  <Box sx={{ p: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                                      Quick Fix Commands:
                                    </Typography>
                                    <Box sx={{ fontFamily: 'monospace', fontSize: '11px', whiteSpace: 'pre-wrap' }}>
                                      {getFixInstructions(vuln).commands.slice(0, 5).join('\n')}
                                    </Box>
                                    <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                                      Click to view full instructions
                                    </Typography>
                                  </Box>
                                }
                                arrow
                                placement="left"
                              >
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => setExpandedVuln(isExpanded ? null : index)}
                                  sx={{ 
                                    textTransform: 'none',
                                    fontSize: '12px',
                                    py: 0.5,
                                    px: 1.5
                                  }}
                                >
                                  View Fix
                                </Button>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatTimestamp(vuln.vulnerability?.detected_at)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Tooltip title="View Details">
                                <IconButton 
                                  size="small"
                                  onClick={() => setExpandedVuln(isExpanded ? null : index)}
                                  sx={{ 
                                    color: isExpanded ? '#1976d2' : 'text.secondary',
                                    '&:hover': { backgroundColor: '#e3f2fd' }
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                          
                          {/* Expanded Details Row */}
                          {isExpanded && (
                            <TableRow>
                              <TableCell colSpan={7} sx={{ bgcolor: '#f8f9fa', p: 0 }}>
                                <Box sx={{ p: 3 }}>
                                  <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                        Vulnerability Information
                                      </Typography>
                                      <Box sx={{ pl: 2 }}>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                          <strong>CVE ID:</strong> {vuln.vulnerability?.cve || vuln.vulnerability?.id || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                          <strong>Severity:</strong> {vuln.vulnerability?.severity || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                          <strong>CVSS Score:</strong> {vulnerabilityApiService.formatCvssScore(vuln.vulnerability?.score)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                          <strong>Category:</strong> {vuln.vulnerability?.category || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                          <strong>Published:</strong> {formatTimestamp(vuln.vulnerability?.published_at)}
                                        </Typography>
                                        {vuln.vulnerability?.reference && (
                                          <Box sx={{ mt: 1 }}>
                                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                              <strong>References:</strong>
                                            </Typography>
                                            {vuln.vulnerability.reference.split(', ').map((ref, idx) => (
                                              <Link
                                                key={idx}
                                                href={ref.trim()}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ display: 'block', fontSize: '12px', mb: 0.5 }}
                                              >
                                                <LinkIcon sx={{ fontSize: 12, mr: 0.5 }} />
                                                {ref.trim()}
                                              </Link>
                                            ))}
                                          </Box>
                                        )}
                                      </Box>
                                    </Grid>
                                    
                                    <Grid item xs={12} md={6}>
                                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                        Package Information
                                      </Typography>
                                      <Box sx={{ pl: 2 }}>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                          <strong>Package:</strong> {vuln.package?.name || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                          <strong>Version:</strong> {vuln.package?.version || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                          <strong>Architecture:</strong> {vuln.package?.architecture || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                          <strong>Type:</strong> {vuln.package?.type || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Size:</strong> {vuln.package?.size ? `${(vuln.package.size / 1024).toFixed(1)} KB` : 'N/A'}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                    
                                    {vuln.vulnerability?.description && (
                                      <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                          Description
                                        </Typography>
                                        <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                                          <Typography 
                                            variant="body2" 
                                            sx={{ 
                                              fontSize: '13px',
                                              lineHeight: 1.5
                                            }}
                                          >
                                            {vuln.vulnerability.description}
                                          </Typography>
                                        </Paper>
                                      </Grid>
                                    )}
                                    
                                    {/* Fix Instructions Section */}
                                    <Grid item xs={12}>
                                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#1976d2' }}>
                                        🔧 How to Fix This Vulnerability
                                      </Typography>
                                      <Paper sx={{ p: 2, bgcolor: '#e3f2fd', border: '1px solid #90caf9' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                          Commands to Run:
                                        </Typography>
                                        <Paper sx={{ p: 2, bgcolor: '#1e1e1e', color: '#d4d4d4', fontFamily: 'monospace', fontSize: '12px', mb: 2 }}>
                                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {getFixInstructions(vuln).commands.join('\n')}
                                          </pre>
                                        </Paper>
                                        
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                          Documentation & Resources:
                                        </Typography>
                                        <Box sx={{ pl: 2 }}>
                                          {getFixInstructions(vuln).docs.map((doc, idx) => {
                                            const urlMatch = doc.match(/https?:\/\/[^\s]+/);
                                            const url = urlMatch ? urlMatch[0] : '';
                                            const text = doc.replace(url, '').trim();
                                            
                                            return (
                                              <Box key={idx} sx={{ mb: 0.5 }}>
                                                {url ? (
                                                  <Link
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: 0.5 }}
                                                  >
                                                    <LinkIcon sx={{ fontSize: 14 }} />
                                                    {text || url}
                                                  </Link>
                                                ) : (
                                                  <Typography variant="body2" sx={{ fontSize: '13px' }}>
                                                    {doc}
                                                  </Typography>
                                                )}
                                              </Box>
                                            );
                                          })}
                                        </Box>
                                        
                                        <Alert severity="info" sx={{ mt: 2, fontSize: '12px' }}>
                                          <strong>Note:</strong> Always backup your system before applying updates. Test in a staging environment first if possible.
                                        </Alert>
                                      </Paper>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={filteredVulnerabilities.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AgentVulnerabilities;
